import { isNil, isObject, isArray, isString } from "lodash";
import { formatDateTime } from "../functions/formatDateTime";

export const convertObjectToReadableEntries = (data, options = {}) => {
  const {
    hide = [],
    show = [],
    format = [],
    formatters = {},
    excludeKeys = [],
    // When true, objects/arrays are skipped unless explicitly formatted or a formatter is provided
    skipObjects = false,
    // When true, sorts all object keys alphabetically (A-Z)
    sortKeys = false,
  } = options;

  const allExcludedKeys = [...hide, ...excludeKeys];

  const getCustomFormat = (key) => format.find((f) => f.field === key);

  const formatFieldType = (value, type) => {
    if (isNil(value)) return "N/A";

    const numValue = typeof value === "string" ? parseFloat(value) : value;

    switch (type) {
      case "dollar":
        if (isNaN(numValue)) return String(value);
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(numValue);

      case "percent":
        if (isNaN(numValue)) return String(value);
        return new Intl.NumberFormat("en-US", {
          style: "percent",
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(numValue / 100);

      case "number":
        if (isNaN(numValue)) return String(value);
        return new Intl.NumberFormat("en-US").format(numValue);

      case "date":
        return formatDateTime(value);

      default:
        return String(value);
    }
  };

  const formatPrimitiveValue = (key, value) => {
    if (isNil(value)) return "N/A";

    // Apply format rule (by key name) anywhere in the tree
    const customFormat = getCustomFormat(key);
    if (customFormat) return formatFieldType(value, customFormat.type);

    // Apply formatter (by key name) anywhere in the tree
    if (formatters[key]) return formatters[key](value);

    // Defaults
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "number") return value.toLocaleString();

    return String(value);
  };

  const hasExplicitFormatting = (key) => {
    return Boolean(formatters[key] || getCustomFormat(key));
  };

  const sortObjectKeys = (obj) => {
    if (!sortKeys || !isObject(obj) || isArray(obj)) return obj;

    const sorted = {};
    Object.keys(obj)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
      .forEach((key) => {
        sorted[key] = obj[key];
      });
    return sorted;
  };

  // Recursively walk arrays/objects and format nested items by their keys
  const walk = (value, parentKey = null, depth = 0) => {
    // For primitives, format using the parent key context
    if (isNil(value)) {
      return parentKey ? formatPrimitiveValue(parentKey, value) : "N/A";
    }

    // Strings are primitives (lodash isObject("x") is false, but keep explicit)
    if (!isObject(value) || isString(value)) {
      return parentKey ? formatPrimitiveValue(parentKey, value) : String(value);
    }

    if (isArray(value)) {
      // If caller wants to skip arrays/objects unless explicitly formatted
      if (skipObjects && parentKey && !hasExplicitFormatting(parentKey)) {
        return undefined;
      }

      // If array field has explicit formatting/formatter, let it handle the whole array
      if (parentKey) {
        const customFormat = getCustomFormat(parentKey);
        if (customFormat) return formatFieldType(value, customFormat.type);
        if (formatters[parentKey]) return formatters[parentKey](value);
      }

      // Otherwise, return the full array, recursively formatting nested items
      return value
        .map((item) => walk(item, null, depth + 1))
        .filter((item) => item !== undefined);
    }

    // Plain object
    if (skipObjects && parentKey && !hasExplicitFormatting(parentKey)) {
      return undefined;
    }

    // If object field has explicit formatting/formatter, let it handle the whole object
    if (parentKey) {
      const customFormat = getCustomFormat(parentKey);
      if (customFormat) return formatFieldType(value, customFormat.type);
      if (formatters[parentKey]) return formatters[parentKey](value);
    }

    const out = {};
    const entries = sortKeys
      ? Object.entries(value).sort(([a], [b]) =>
          a.localeCompare(b, undefined, { sensitivity: "base" }),
        )
      : Object.entries(value);

    entries.forEach(([k, v]) => {
      // hide/exclude applies at all nesting levels
      if (allExcludedKeys.includes(k)) return;

      const walked = (() => {
        // For primitives, format based on THIS key name (so nested "amount" formats as dollar, etc.)
        if (isNil(v) || !isObject(v) || isString(v)) {
          return formatPrimitiveValue(k, v);
        }

        // For arrays/objects, recurse and keep full structure (unless skipObjects blocks it)
        return walk(v, k, depth + 1);
      })();

      if (walked !== undefined) {
        out[k] = walked;
      }
    });

    return out;
  };

  if (!data || !isObject(data) || isString(data)) return data;

  // Root-level inclusion logic:
  // - If `show` is provided, only include those keys at the root.
  // - Otherwise include all root keys except excluded ones.
  const rootOut = {};
  const rootEntries = sortKeys
    ? Object.entries(data).sort(([a], [b]) =>
        a.localeCompare(b, undefined, { sensitivity: "base" }),
      )
    : Object.entries(data);

  rootEntries.forEach(([key, value]) => {
    if (show.length > 0 && !show.includes(key)) return;
    if (allExcludedKeys.includes(key)) return;

    const walked = (() => {
      if (isNil(value) || !isObject(value) || isString(value)) {
        return formatPrimitiveValue(key, value);
      }
      return walk(value, key, 1);
    })();

    if (walked !== undefined) {
      rootOut[key] = walked;
    }
  });

  return rootOut;
};
